import { faCoinFront } from "@fortawesome/pro-regular-svg-icons";
import { faAngleDown } from "@fortawesome/sharp-solid-svg-icons";
import { Box, Button, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { PRIMARY_NAVIGATION_HEADER_ROW_HEIGHT } from "@/components/nav/constants";
import {
  bindMenuTrigger,
  CircularProgress,
  Icon,
  MenuState,
  Typography,
} from "@/components/ui-v2";
import { humanNumberISO } from "@/utils/humanize";

import { WorkspaceDropdownInfo } from "./types";
import { mapConsumptionToProgressColor } from "./utils";
import { getWorkspaceDisplayName, WorkspaceAvatar } from "./WorkspaceDropdown";

interface WorkspaceDropdownButtonProps {
  mainMenuState: MenuState;
  workspaces: {
    current: WorkspaceDropdownInfo | null;
    nonCurrent: WorkspaceDropdownInfo[];
    all: WorkspaceDropdownInfo[];
  };
  percentageRemaining: number;
  remainingUnits: number | null;
}

export const WorkspaceDropdownButton = (
  props: WorkspaceDropdownButtonProps,
) => {
  const { mainMenuState, workspaces, percentageRemaining, remainingUnits } =
    props;

  const theme = useTheme();

  return (
    <Button
      variant="text"
      color="secondary"
      size="large"
      sx={{
        p: 0,
        transition: "all 300ms ease-out",
        fontWeight: 600,
        minWidth: 0,
        height: PRIMARY_NAVIGATION_HEADER_ROW_HEIGHT,
        border: "none",
        justifyContent: "flex-start",
        backgroundColor: mainMenuState.open
          ? theme.palette.secondary[7]
          : "transparent",
        boxShadow: mainMenuState.open
          ? `inset 0 0 0 0.5px ${theme.palette.secondary[6]}`
          : "none",

        "&:hover": {
          backgroundColor: theme.palette.secondary[7],
          boxShadow: `inset 0 0 0 0.5px ${theme.palette.secondary[6]}`,
        },

        "& .MuiButton-startIcon": {
          transition: "all 300ms ease-out",
        },
        "& .MuiButton-icon": {
          marginLeft: 0,
          marginRight: theme.spacing(2),
        },
      }}
      startIcon={
        <Box
          component="span"
          sx={{
            height: theme.spacing(12),
            width: theme.spacing(12),
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* TODO: We are using an older version of MUI so we need to render a separate progress bar for the track */}
          <>
            <CircularProgress
              variant="determinate"
              value={100}
              size={theme.spacing(8)}
              thickness={2}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                margin: "auto",
                color: theme.palette.secondary[2],
                opacity: 0.2,
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
              }}
            />
            <CircularProgress
              variant="determinate"
              value={percentageRemaining}
              size={theme.spacing(8)}
              thickness={2}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                margin: "auto",
                color: mapConsumptionToProgressColor(
                  percentageRemaining,
                  theme,
                ),
                "& .MuiCircularProgress-circle": {
                  strokeLinecap: "round",
                },
              }}
            />
          </>
          <Box
            component="span"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1,
            }}
          >
            {workspaces.current && (
              <WorkspaceAvatar
                isLink={false}
                workspace={workspaces.current}
                sx={{
                  width: theme.spacing(4),
                  height: theme.spacing(4),
                  fontSize: theme.spacing(3),
                }}
                disableTooltip={true}
                tooltipPlacement="right"
              />
            )}
          </Box>
        </Box>
      }
      endIcon={
        <span
          style={{
            fontSize: "0.85em",
            color: theme.palette.secondary[3],
            marginLeft: 8,
          }}
        >
          <Icon icon={faAngleDown} />
        </span>
      }
      {...bindMenuTrigger(mainMenuState)}
    >
      <Stack direction="column" sx={{ maxWidth: 200, minWidth: 0 }}>
        <Typography
          variant="regular"
          color="secondary.2"
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          lineHeight={1.5}
        >
          {workspaces.current
            ? getWorkspaceDisplayName(workspaces.current)
            : ""}
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            color:
              remainingUnits === null
                ? theme.palette.secondary[3]
                : remainingUnits === 0
                  ? theme.palette.error.light
                  : theme.palette.primary.light,
          }}
        >
          <Icon icon={faCoinFront} />
          <Typography
            variant="micro"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            lineHeight={1.5}
          >
            {remainingUnits !== null ? humanNumberISO(remainingUnits) : "-"}
          </Typography>
        </Stack>
      </Stack>
    </Button>
  );
};
